import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, User, Mail, Phone, MoreVertical, Edit, Trash2, Shield, Send } from "lucide-react";
import { getPlayerDisplayName } from "@/utils/displayUtils";
import { EditPlayerDialog } from "./EditPlayerDialog";
import { DeletePlayerDialog } from "./DeletePlayerDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Player {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  auth_user_id?: string;
  group_id?: string;
  profiles?: {
    display_name?: string;
  } | null;
}

interface PlayerListProps {
  players: Player[];
  onUpdate: () => void;
  onAddPlayer: () => void;
  currentUserId?: string;
}

export const PlayerList = ({ players, onUpdate, onAddPlayer, currentUserId }: PlayerListProps) => {
  const [editPlayer, setEditPlayer] = useState<Player | null>(null);
  const [deletePlayer, setDeletePlayer] = useState<Player | null>(null);
  const [resendingInvite, setResendingInvite] = useState<string | null>(null);
  const { toast } = useToast();

  const handleResendInvite = async (player: Player) => {
    if (!player.email) return;
    
    setResendingInvite(player.id);
    try {
      const { error } = await supabase.functions.invoke('invite-player', {
        body: {
          email: player.email,
          redirectTo: 'https://splitpot.lovable.app/?invite=1'
        }
      });
      
      if (error) throw error;
      
      toast({
        title: "Invitation Sent",
        description: `Invitation email sent to ${player.email}`,
      });
    } catch (error) {
      console.error('Error resending invite:', error);
      toast({
        title: "Error",
        description: "Failed to send invitation email",
        variant: "destructive"
      });
    } finally {
      setResendingInvite(null);
    }
  };
  if (players.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No players yet</h3>
          <p className="text-muted-foreground mb-4">Add players to start creating games</p>
          <Button onClick={onAddPlayer}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Player
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Players ({players.length})</h3>
        <Button onClick={onAddPlayer}>
          <Plus className="h-4 w-4 mr-2" />
          Add Player
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {players.map((player) => (
          <Card key={player.id}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  {getPlayerDisplayName(player)}{player.auth_user_id === currentUserId ? " (You)" : ""}
                  {player.auth_user_id && (
                    <div title="Linked to authenticated account">
                      <Shield className="h-3 w-3 ml-2 text-primary" />
                    </div>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditPlayer(player)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Player
                    </DropdownMenuItem>
                    {player.email && !player.auth_user_id && (
                      <DropdownMenuItem 
                        onClick={() => handleResendInvite(player)}
                        disabled={resendingInvite === player.id}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {resendingInvite === player.id ? "Sending..." : "Resend Invite"}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => setDeletePlayer(player)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Player
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {player.email && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Mail className="h-3 w-3 mr-2" />
                  <span className="truncate">{player.email}</span>
                </div>
              )}
              {player.phone && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Phone className="h-3 w-3 mr-2" />
                  {player.phone}
                </div>
              )}
              {player.auth_user_id && (
                <Badge variant="outline" className="text-xs border-primary text-primary">
                  <Shield className="h-2 w-2 mr-1" />
                  Authenticated Account
                </Badge>
              )}
              {player.email && !player.auth_user_id && (
                <Badge variant="warning" className="text-xs">
                  Invite Pending
                </Badge>
              )}
              {!player.email && !player.phone && !player.auth_user_id && (
                <Badge variant="secondary" className="text-xs">
                  No contact info
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {editPlayer && (
        <EditPlayerDialog
          open={!!editPlayer}
          onOpenChange={() => setEditPlayer(null)}
          player={editPlayer}
          onPlayerUpdated={onUpdate}
        />
      )}

      {deletePlayer && (
        <DeletePlayerDialog
          open={!!deletePlayer}
          onOpenChange={() => setDeletePlayer(null)}
          player={deletePlayer}
          onPlayerDeleted={onUpdate}
        />
      )}
    </div>
  );
};